import * as THREE from 'three';
import { EngineConfig, UpdateContext, CelestialConfig, SimulationStats } from '../types';
import { InputManager } from '../input/InputManager';
import { CameraController } from '../camera/CameraController';
import { PhysicsWorld } from '../simulation/PhysicsWorld';
import { CelestialBody } from '../objects/CelestialBody';
import { PlayerShip } from '../objects/PlayerShip';
import { Projectile } from '../objects/Projectile';
import { EnemyProjectile } from '../objects/EnemyProjectile';
import { TargetableObject, TargetType } from '../objects/TargetableObject';
import { Explosion } from '../objects/Explosion';
import { COLORS, RENDER_CONFIG, KEY_BINDINGS, DEFAULT_SIMULATION_CONFIG, DEFAULT_CAMERA_CONFIG } from '../constants';

/**
 * Main game engine - orchestrates rendering, physics, and input.
 */
export class Engine {
  // Core systems
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private inputManager: InputManager;
  private cameraController: CameraController;
  private physicsWorld: PhysicsWorld;

  // Game objects
  private celestialBodies: Map<string, CelestialBody> = new Map();
  private selectedBody: CelestialBody | null = null;

  // Scene objects
  private starField?: THREE.Points;
  private ambientLight: THREE.AmbientLight;
  private sunLight?: THREE.PointLight;
  private playerShip: PlayerShip;

  // Timing
  private clock: THREE.Clock;
  private lastTime: number = 0;
  private frameCount: number = 0;
  private fps: number = 60;

  // State
  private running: boolean = false;
  private container: HTMLElement;

  // Raycaster for selection
  private raycaster: THREE.Raycaster;

  // Speed tracking
  private lastCameraPosition: THREE.Vector3 = new THREE.Vector3();
  private currentSpeed: number = 0;

  // Projectiles
  private projectiles: Map<string, Projectile> = new Map();
  private lastFireTime: number = 0;
  private fireRate: number = 0.15; // Seconds between shots

  // Targets and explosions
  private targets: Map<string, TargetableObject> = new Map();
  private explosions: Map<string, Explosion> = new Map();

  // Enemy projectiles
  private enemyProjectiles: Map<string, EnemyProjectile> = new Map();

  // Player health system
  private playerHealth: number = 100;
  private playerMaxHealth: number = 100;
  private playerDamageFlash: number = 0; // For visual feedback
  private lastPlayerDamageTime: number = 0;

  // Touch firing state
  private touchFiring: boolean = false;

  constructor(config: Partial<EngineConfig> = {}) {
    this.container = config.container ?? document.getElementById('app')!;

    // Initialize renderer
    this.renderer = new THREE.WebGLRenderer({
      antialias: config.antialias ?? true,
      powerPreference: 'high-performance',
    });
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.setPixelRatio(config.pixelRatio ?? Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1;
    this.container.appendChild(this.renderer.domElement);

    // Initialize scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(COLORS.background);

    // Initialize input
    this.inputManager = new InputManager(this.renderer.domElement);

    // Initialize camera
    this.cameraController = new CameraController(
      this.container,
      this.inputManager,
      config.camera ?? DEFAULT_CAMERA_CONFIG
    );

    // Initialize physics
    this.physicsWorld = new PhysicsWorld(config.simulation ?? DEFAULT_SIMULATION_CONFIG);

    // Initialize lighting - bright ambient so all objects are visible
    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(this.ambientLight);

    // Add hemisphere light for better depth perception
    const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.4);
    hemiLight.position.set(0, 200, 0);
    this.scene.add(hemiLight);

    // Initialize raycaster
    this.raycaster = new THREE.Raycaster();

    // Initialize clock
    this.clock = new THREE.Clock();

    // Create star field background
    this.createStarField();

    // Add camera to scene (required for camera children to render)
    this.scene.add(this.cameraController.camera);

    // Create player ship cockpit and attach to camera
    this.playerShip = new PlayerShip();
    this.playerShip.mesh.position.set(0, -0.5, -2); // Position in front of camera, slightly below center
    this.playerShip.mesh.scale.set(0.5, 0.5, 0.5); // Scale down for better proportions
    this.cameraController.camera.add(this.playerShip.mesh);

    // Bind events
    this.bindEvents();

    // Handle resize
    window.addEventListener('resize', this.onResize);
  }

  private createStarField(): void {
    const starCount = RENDER_CONFIG.starFieldCount;
    const radius = RENDER_CONFIG.starFieldRadius;

    const positions = new Float32Array(starCount * 3);
    const colors = new Float32Array(starCount * 3);
    const sizes = new Float32Array(starCount);

    for (let i = 0; i < starCount; i++) {
      // Random position on a sphere
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = radius * (0.5 + Math.random() * 0.5);

      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);

      // Vary star colors slightly
      const colorVariation = 0.8 + Math.random() * 0.4;
      colors[i * 3] = colorVariation;
      colors[i * 3 + 1] = colorVariation;
      colors[i * 3 + 2] = 0.9 + Math.random() * 0.1;

      // Vary star sizes
      sizes[i] = 0.5 + Math.random() * 2;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const material = new THREE.PointsMaterial({
      size: 2,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      sizeAttenuation: false,
    });

    this.starField = new THREE.Points(geometry, material);
    this.scene.add(this.starField);
  }

  private bindEvents(): void {
    // Selection via tap/click
    this.inputManager.on('tap', (e) => {
      if (e.position) {
        this.handleSelection(e.position);
      }
    });

    // Long press for context menu (mobile)
    this.inputManager.on('longpress', (e) => {
      if (e.position && this.selectedBody) {
        // Could open context menu here
        console.log('Long press on', this.selectedBody.name);
      }
    });

    // Keyboard shortcuts
    this.inputManager.on('keydown', (e) => {
      if (!e.key) return;

      // Toggle pause
      if (KEY_BINDINGS.togglePause.includes(e.key)) {
        this.physicsWorld.setPaused(!this.physicsWorld.isPaused());
      }

      // Time scale
      if (KEY_BINDINGS.speedUp.includes(e.key)) {
        const scale = this.physicsWorld.getTimeScale();
        this.physicsWorld.setTimeScale(Math.min(scale * 2, 64));
      }
      if (KEY_BINDINGS.slowDown.includes(e.key)) {
        const scale = this.physicsWorld.getTimeScale();
        this.physicsWorld.setTimeScale(Math.max(scale / 2, 0.125));
      }

      // Camera mode toggle (C key cycles: ship -> orbit -> ship)
      if (KEY_BINDINGS.toggleCameraMode.includes(e.key)) {
        const mode = this.cameraController.getMode();
        if (mode === 'ship') {
          this.cameraController.setMode('orbit');
        } else {
          this.cameraController.setMode('ship');
        }
      }

      // Focus on selected
      if (KEY_BINDINGS.focusSelected.includes(e.key) && this.selectedBody) {
        this.cameraController.lookAt(this.selectedBody.getPosition());
        this.cameraController.setDistance(this.selectedBody.config.radius * 5);
      }

      // Reset camera
      if (KEY_BINDINGS.resetCamera.includes(e.key)) {
        this.cameraController.resetView();
      }

      // Deselect
      if (KEY_BINDINGS.deselect.includes(e.key)) {
        this.setSelectedBody(null);
      }

      // Delete selected
      if (KEY_BINDINGS.delete.includes(e.key) && this.selectedBody) {
        this.removeCelestialBody(this.selectedBody.id);
        this.setSelectedBody(null);
      }
    });

    // Collision events
    this.physicsWorld.onCollision((event) => {
      console.log(`Collision: ${event.bodyA.id} <-> ${event.bodyB.id}`);
      // Could add explosion effects, merge bodies, etc.
    });
  }

  private handleSelection(screenPosition: { x: number; y: number }): void {
    // Convert to NDC
    const ndc = new THREE.Vector2(
      (screenPosition.x / this.container.clientWidth) * 2 - 1,
      -(screenPosition.y / this.container.clientHeight) * 2 + 1
    );

    this.raycaster.setFromCamera(ndc, this.cameraController.camera);

    // Get all meshes
    const meshes: THREE.Object3D[] = [];
    this.celestialBodies.forEach((body) => {
      meshes.push(body.mesh);
    });

    const intersects = this.raycaster.intersectObjects(meshes, true);

    if (intersects.length > 0) {
      // Find the celestial body from the intersected object
      let obj = intersects[0].object;
      while (obj.parent && !obj.userData.celestialBody) {
        obj = obj.parent;
      }

      if (obj.userData.celestialBody) {
        this.setSelectedBody(obj.userData.celestialBody);
      }
    } else {
      this.setSelectedBody(null);
    }
  }

  private setSelectedBody(body: CelestialBody | null): void {
    if (this.selectedBody) {
      this.selectedBody.setSelected(false);
    }

    this.selectedBody = body;

    if (this.selectedBody) {
      this.selectedBody.setSelected(true);
    }
  }

  private onResize = (): void => {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.renderer.setSize(width, height);
    this.cameraController.resize(width, height);
  };

  private update = (): void => {
    if (!this.running) return;

    requestAnimationFrame(this.update);

    const deltaTime = this.clock.getDelta();
    const elapsedTime = this.clock.getElapsedTime();

    // Calculate FPS
    this.frameCount++;
    if (elapsedTime - this.lastTime >= 1) {
      this.fps = this.frameCount;
      this.frameCount = 0;
      this.lastTime = elapsedTime;
    }

    // Get input state
    const inputState = this.inputManager.getState();

    // Create update context
    const context: UpdateContext = {
      deltaTime,
      elapsedTime,
      input: inputState,
      simulation: this.physicsWorld.getConfig(),
    };

    // Update systems
    this.cameraController.update(deltaTime, inputState);
    this.physicsWorld.update(deltaTime);

    // Update celestial bodies
    this.celestialBodies.forEach((body) => {
      body.update(deltaTime);
    });

    // Handle firing (keyboard OR touch)
    if (this.inputManager.isKeyDown(KEY_BINDINGS.fire) || this.touchFiring) {
      this.tryFireProjectile(elapsedTime);
    }

    // Update projectiles
    this.updateProjectiles(deltaTime);

    // Update targets (pass elapsed time for AI)
    this.updateTargets(deltaTime, elapsedTime);

    // Update enemy projectiles
    this.updateEnemyProjectiles(deltaTime);

    // Check projectile-target collisions
    this.checkProjectileCollisions();

    // Check enemy projectile-player collisions
    this.checkEnemyProjectileCollisions();

    // Update explosions
    this.updateExplosions(deltaTime);

    // Update damage flash
    if (this.playerDamageFlash > 0) {
      this.playerDamageFlash -= deltaTime * 3;
      if (this.playerDamageFlash < 0) this.playerDamageFlash = 0;
    }

    // Calculate camera speed
    const cameraPos = this.cameraController.camera.position;
    const distance = cameraPos.distanceTo(this.lastCameraPosition);
    this.currentSpeed = distance / deltaTime;
    this.lastCameraPosition.copy(cameraPos);

    // Update player ship - check if moving via keyboard, pass camera orientation, speed, and simulation state
    const isMoving = this.inputManager.isKeyDown(['KeyW', 'KeyS', 'KeyA', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);
    const isFiring = this.inputManager.isKeyDown(KEY_BINDINGS.fire) || this.touchFiring;
    const timeScale = this.physicsWorld.getTimeScale();
    const isPaused = this.physicsWorld.isPaused();

    // Get thrust state for thruster diagram
    const thrustState = {
      forward: this.inputManager.isKeyDown(KEY_BINDINGS.forward),
      backward: this.inputManager.isKeyDown(KEY_BINDINGS.backward),
      left: this.inputManager.isKeyDown(KEY_BINDINGS.left),
      right: this.inputManager.isKeyDown(KEY_BINDINGS.right),
      up: this.inputManager.isKeyDown(KEY_BINDINGS.up),
      down: this.inputManager.isKeyDown(KEY_BINDINGS.down),
    };

    // Get ship velocity for velocity arrow
    const shipVelocity = this.cameraController.getShipVelocity();

    this.playerShip.update(
      deltaTime,
      isMoving,
      this.cameraController.camera.quaternion,
      this.currentSpeed,
      timeScale,
      isPaused,
      isFiring,
      thrustState,
      shipVelocity,
      this.playerHealth / this.playerMaxHealth,
      this.playerDamageFlash
    );

    // Clear frame input state
    this.inputManager.update();

    // Render
    this.renderer.render(this.scene, this.cameraController.camera);
  };

  // Public API

  public start(): void {
    this.running = true;
    this.clock.start();
    this.update();
  }

  public stop(): void {
    this.running = false;
    this.clock.stop();
  }

  public addCelestialBody(config: CelestialConfig): CelestialBody {
    const body = new CelestialBody(config);

    this.scene.add(body.mesh);
    this.celestialBodies.set(body.id, body);
    this.physicsWorld.addBody(body.physicsBody);

    // If it's a star, add point light
    if (config.type === 'star') {
      const light = new THREE.PointLight(config.color, 2, 0, 0.5);
      light.position.copy(body.mesh.position);
      light.castShadow = true;
      light.shadow.mapSize.width = RENDER_CONFIG.shadowMapSize;
      light.shadow.mapSize.height = RENDER_CONFIG.shadowMapSize;
      body.mesh.add(light);
      this.sunLight = light;
    }

    return body;
  }

  public removeCelestialBody(id: string): void {
    const body = this.celestialBodies.get(id);
    if (!body) return;

    this.scene.remove(body.mesh);
    this.celestialBodies.delete(id);
    this.physicsWorld.removeBody(id);
    body.dispose();
  }

  public getCelestialBody(id: string): CelestialBody | undefined {
    return this.celestialBodies.get(id);
  }

  public getAllCelestialBodies(): CelestialBody[] {
    return Array.from(this.celestialBodies.values());
  }

  public getSelectedBody(): CelestialBody | null {
    return this.selectedBody;
  }

  public getPhysicsWorld(): PhysicsWorld {
    return this.physicsWorld;
  }

  public getCameraController(): CameraController {
    return this.cameraController;
  }

  public getInputManager(): InputManager {
    return this.inputManager;
  }

  public setTouchFiring(firing: boolean): void {
    this.touchFiring = firing;
  }

  public getStats(): SimulationStats {
    return {
      fps: this.fps,
      bodyCount: this.celestialBodies.size,
      simulationTime: this.clock.getElapsedTime() * this.physicsWorld.getTimeScale(),
      realTime: this.clock.getElapsedTime(),
    };
  }

  public createOrbitalBody(
    config: CelestialConfig,
    centralBodyId: string
  ): CelestialBody | null {
    const centralBody = this.celestialBodies.get(centralBodyId);
    if (!centralBody) return null;

    const body = this.addCelestialBody(config);

    // Calculate and set orbital velocity
    const orbitalVelocity = this.physicsWorld.calculateOrbitalVelocity(
      body.physicsBody,
      centralBody.physicsBody
    );
    body.setVelocity(orbitalVelocity);

    return body;
  }

  private tryFireProjectile(currentTime: number): void {
    // Check fire rate
    if (currentTime - this.lastFireTime < this.fireRate) {
      return;
    }
    this.lastFireTime = currentTime;

    // Get camera position and direction
    const cameraPos = this.cameraController.camera.position.clone();
    const cameraDir = new THREE.Vector3(0, 0, -1).applyQuaternion(this.cameraController.camera.quaternion);

    // Offset spawn position slightly in front of camera
    const spawnPos = cameraPos.clone().add(cameraDir.clone().multiplyScalar(5));

    // Get ship velocity for inheritance (makes physics more realistic)
    const shipVelocity = this.cameraController.getMode() === 'ship'
      ? cameraDir.clone().multiplyScalar(this.currentSpeed * 0.5)
      : new THREE.Vector3();

    // Create projectile
    const projectile = new Projectile(spawnPos, cameraDir, shipVelocity);

    // Add to scene
    this.scene.add(projectile.mesh);
    this.scene.add(projectile.getTrail());

    // Add to physics world
    this.physicsWorld.addBody(projectile.physicsBody);

    // Track projectile
    this.projectiles.set(projectile.id, projectile);
  }

  private updateProjectiles(deltaTime: number): void {
    const toRemove: string[] = [];

    this.projectiles.forEach((projectile, id) => {
      projectile.update(deltaTime);

      if (!projectile.isAlive()) {
        toRemove.push(id);
      }
    });

    // Clean up dead projectiles
    toRemove.forEach((id) => {
      const projectile = this.projectiles.get(id);
      if (projectile) {
        this.scene.remove(projectile.mesh);
        this.scene.remove(projectile.getTrail());
        this.physicsWorld.removeBody(id);
        projectile.dispose();
        this.projectiles.delete(id);
      }
    });
  }

  private updateTargets(deltaTime: number, elapsedTime: number): void {
    const cameraPos = this.cameraController.camera.position;

    this.targets.forEach((target) => {
      target.update(deltaTime, cameraPos, elapsedTime);
    });
  }

  private updateEnemyProjectiles(deltaTime: number): void {
    const toRemove: string[] = [];

    this.enemyProjectiles.forEach((projectile, id) => {
      projectile.update(deltaTime);

      if (!projectile.isAlive()) {
        toRemove.push(id);
      }
    });

    // Clean up dead projectiles
    toRemove.forEach((id) => {
      const projectile = this.enemyProjectiles.get(id);
      if (projectile) {
        this.scene.remove(projectile.mesh);
        this.scene.remove(projectile.getTrail());
        this.physicsWorld.removeBody(id);
        projectile.dispose();
        this.enemyProjectiles.delete(id);
      }
    });
  }

  private checkEnemyProjectileCollisions(): void {
    const playerPos = this.cameraController.camera.position;
    const playerRadius = 3; // Player hitbox radius
    const toRemove: string[] = [];

    this.enemyProjectiles.forEach((projectile, id) => {
      const projPos = projectile.mesh.position;
      const distance = projPos.distanceTo(playerPos);

      if (distance < playerRadius + projectile.physicsBody.radius) {
        // Player hit!
        toRemove.push(id);
        this.damagePlayer(10);
      }
    });

    // Remove hit projectiles
    toRemove.forEach((id) => {
      const projectile = this.enemyProjectiles.get(id);
      if (projectile) {
        this.scene.remove(projectile.mesh);
        this.scene.remove(projectile.getTrail());
        this.physicsWorld.removeBody(id);
        projectile.dispose();
        this.enemyProjectiles.delete(id);
      }
    });
  }

  private damagePlayer(amount: number): void {
    this.playerHealth -= amount;
    this.playerDamageFlash = 1;
    this.lastPlayerDamageTime = this.clock.getElapsedTime();

    if (this.playerHealth <= 0) {
      this.playerHealth = 0;
      // Player dead - could respawn or game over
      console.log('Player destroyed!');
      // Respawn after a moment
      setTimeout(() => {
        this.playerHealth = this.playerMaxHealth;
      }, 2000);
    }
  }

  private spawnEnemyProjectile(position: THREE.Vector3, direction: THREE.Vector3, velocity: THREE.Vector3): void {
    const projectile = new EnemyProjectile(position, direction, velocity);

    this.scene.add(projectile.mesh);
    this.scene.add(projectile.getTrail());
    this.physicsWorld.addBody(projectile.physicsBody);
    this.enemyProjectiles.set(projectile.id, projectile);
  }

  private checkProjectileCollisions(): void {
    const projectilesToRemove: string[] = [];
    const targetsToDestroy: TargetableObject[] = [];

    this.projectiles.forEach((projectile, projectileId) => {
      if (projectilesToRemove.includes(projectileId)) return;

      const projPos = projectile.mesh.position;
      // Use larger hit radius for more forgiving collision
      const projRadius = projectile.physicsBody.radius * 3;

      this.targets.forEach((target) => {
        if (!target.isAlive()) return;
        if (projectilesToRemove.includes(projectileId)) return;

        const targetPos = target.mesh.position;
        // Double target radius for easier hits
        const targetRadius = target.physicsBody.radius * 1.5;

        // Simple sphere collision with generous radii
        const distance = projPos.distanceTo(targetPos);
        if (distance < projRadius + targetRadius) {
          // Hit!
          projectilesToRemove.push(projectileId);

          const destroyed = target.takeDamage(1);
          if (destroyed) {
            targetsToDestroy.push(target);
          }
        }
      });
    });

    // Remove hit projectiles
    projectilesToRemove.forEach((id) => {
      const projectile = this.projectiles.get(id);
      if (projectile) {
        this.scene.remove(projectile.mesh);
        this.scene.remove(projectile.getTrail());
        this.physicsWorld.removeBody(id);
        projectile.dispose();
        this.projectiles.delete(id);
      }
    });

    // Destroy targets and spawn explosions
    targetsToDestroy.forEach((target) => {
      this.destroyTarget(target);
    });
  }

  private destroyTarget(target: TargetableObject): void {
    const position = target.getPosition();
    const velocity = target.physicsBody.velocity.clone();
    const size = target.physicsBody.radius;

    // Remove target
    this.scene.remove(target.mesh);
    this.physicsWorld.removeBody(target.id);
    target.dispose();
    this.targets.delete(target.id);

    // Create explosion
    const explosion = new Explosion(position, size, velocity);
    this.scene.add(explosion.mesh);

    // Add debris meshes to scene and physics
    explosion.getDebrisMeshes().forEach((debris) => {
      this.scene.add(debris);
    });
    explosion.debrisBodies.forEach((body) => {
      this.physicsWorld.addBody(body);
    });

    this.explosions.set(explosion.id, explosion);
  }

  private updateExplosions(deltaTime: number): void {
    const toRemove: string[] = [];

    this.explosions.forEach((explosion, id) => {
      explosion.update(deltaTime);

      if (!explosion.isAlive()) {
        toRemove.push(id);
      }
    });

    // Clean up dead explosions
    toRemove.forEach((id) => {
      const explosion = this.explosions.get(id);
      if (explosion) {
        this.scene.remove(explosion.mesh);

        // Remove debris
        explosion.getDebrisMeshes().forEach((debris) => {
          this.scene.remove(debris);
        });
        explosion.debrisBodies.forEach((body) => {
          this.physicsWorld.removeBody(body.id);
        });

        explosion.dispose();
        this.explosions.delete(id);
      }
    });
  }

  public spawnTarget(type: TargetType, position: THREE.Vector3, velocity?: THREE.Vector3): TargetableObject {
    const target = new TargetableObject(type, position, velocity);

    // Set up the firing callback so enemies can shoot
    target.onFireProjectile = (pos, dir, vel) => {
      this.spawnEnemyProjectile(pos, dir, vel);
    };

    this.scene.add(target.mesh);
    this.physicsWorld.addBody(target.physicsBody);
    this.targets.set(target.id, target);

    return target;
  }

  public spawnRandomTargets(count: number = 5): void {
    const types: TargetType[] = ['fighter', 'fighter', 'fighter', 'cruiser', 'station'];

    for (let i = 0; i < count; i++) {
      const type = types[Math.floor(Math.random() * types.length)];

      // Random position around the solar system
      const distance = 100 + Math.random() * 300;
      const angle = Math.random() * Math.PI * 2;
      const height = (Math.random() - 0.5) * 100;

      const position = new THREE.Vector3(
        Math.cos(angle) * distance,
        height,
        Math.sin(angle) * distance
      );

      // Small random velocity for fighters/cruisers
      const velocity = type !== 'station'
        ? new THREE.Vector3(
            (Math.random() - 0.5) * 10,
            (Math.random() - 0.5) * 5,
            (Math.random() - 0.5) * 10
          )
        : new THREE.Vector3();

      this.spawnTarget(type, position, velocity);
    }
  }

  public dispose(): void {
    this.stop();

    // Clean up all projectiles
    this.projectiles.forEach((projectile) => {
      this.scene.remove(projectile.mesh);
      this.scene.remove(projectile.getTrail());
      projectile.dispose();
    });
    this.projectiles.clear();

    // Clean up all bodies
    this.celestialBodies.forEach((body) => {
      body.dispose();
    });
    this.celestialBodies.clear();

    // Clean up star field
    if (this.starField) {
      this.starField.geometry.dispose();
      (this.starField.material as THREE.Material).dispose();
    }

    // Clean up renderer
    this.renderer.dispose();

    // Clean up input
    this.inputManager.destroy();

    // Remove resize listener
    window.removeEventListener('resize', this.onResize);
  }
}
